import { useRef, useState } from 'react';
import { Point, ToolType, MarkupState, SymbolType, SystemType, TradeSymbol, Line } from '@shared/types/tsk';

const DRAG_THRESHOLD = 8;
const HIT_RADIUS_PX = 48;

interface Selection {
  id: string;
  type: 'symbol' | 'node';
}

export function useEditorGestures(options: {
  getCoords: (clientX: number, clientY: number) => { local: Point; world: Point };
  getLocalCoords: (clientX: number, clientY: number) => Point;
  snapPoint: (point: Point, opts?: { excludeId?: string }) => Point & { attachedSymbolId?: string };
  viewportRef: React.MutableRefObject<{ scale: number; offset: Point }>;
  activeTool: ToolType;
  activeSymbol: SymbolType;
  activeSystem: SystemType;
  markupRef: React.MutableRefObject<MarkupState>;
  draftPoints: Point[] | null;
  selection: Selection | null;
  updateSelection: (sel: Selection | null) => void;
  setMarkup: (updater: (prev: MarkupState) => MarkupState, pushToHistory?: boolean) => void;
  setDraftPoints: (updater: (prev: Point[] | null) => Point[] | null) => void;
  setScale: (s: number) => void;
  setOffset: (updater: Point | ((prev: Point) => Point)) => void;
}) {
  const {
    getCoords, getLocalCoords, snapPoint, viewportRef, activeTool, activeSymbol, activeSystem,
    markupRef, draftPoints, selection, updateSelection, setMarkup, setDraftPoints,
    setScale, setOffset
  } = options;

  const activePointers = useRef<Map<number, Point>>(new Map());
  const lastPinchDist = useRef<number | null>(null);
  const lastPinchMidpoint = useRef<Point | null>(null);
  const gestureLock = useRef<boolean>(false);
  
  const gestureRef = useRef({
    isActive: false,
    pointerId: null as number | null,
    startScreenPos: { x: 0, y: 0 },
    hitTargetId: null as string | null,
    hitTargetType: null as 'symbol' | 'node' | null,
    wasMoved: false
  });

  const [isPanning, setIsPanning] = useState(false);
  const [lastMousePos, setLastMousePos] = useState<Point>({ x: 0, y: 0 });
  const [draggingSymbolId, setDraggingSymbolId] = useState<string | null>(null);
  const [activeGrip, setActiveGrip] = useState<{ lineId: string, pointIndex: number } | null>(null);
  const [cursorPos, setCursorPos] = useState<Point | null>(null);
  const [isSnappedToSymbol, setIsSnappedToSymbol] = useState<string | null>(null);

  const resetGestures = () => {
    setIsPanning(false);
    setDraggingSymbolId(null);
    setActiveGrip(null);
    setIsSnappedToSymbol(null);
    setCursorPos(null);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('.floating-ui') || target.closest('button')) return;
    target.setPointerCapture(e.pointerId);
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const isMiddleClick = e.button === 1;
    if (activePointers.current.size > 1 || isMiddleClick) {
      gestureLock.current = true;
      gestureRef.current.isActive = false;
      if (isMiddleClick) {
        setIsPanning(true);
        setLastMousePos({ x: e.clientX, y: e.clientY });
        gestureRef.current = {
          isActive: true,
          pointerId: e.pointerId,
          startScreenPos: { x: e.clientX, y: e.clientY },
          hitTargetId: null,
          hitTargetType: null,
          wasMoved: true
        };
      } else {
        setIsPanning(false);
      }
      setDraggingSymbolId(null);
      setActiveGrip(null);
      if (activePointers.current.size === 2) {
        const points = Array.from(activePointers.current.values()) as Point[];
        lastPinchDist.current = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        lastPinchMidpoint.current = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
      }
      return;
    }
    const { world } = getCoords(e.clientX, e.clientY);
    const hitRadiusWorld = HIT_RADIUS_PX / viewportRef.current.scale;
    let foundHitId: string | null = null;
    let foundHitType: 'symbol' | 'node' | null = null;
    if (activeTool === ToolType.SYMBOL) {
      for (const symbol of markupRef.current.symbols) {
        const dist = Math.hypot(symbol.position.x - world.x, symbol.position.y - world.y);
        if (dist < hitRadiusWorld) {
          foundHitId = symbol.id;
          foundHitType = 'symbol';
          setDraggingSymbolId(symbol.id);
          break;
        }
      }
    }
    if (!foundHitId && activeTool === ToolType.PAN && !draftPoints) {
      for (const line of markupRef.current.lines) {
        for (let i = 0; i < line.points.length; i++) {
          const p = line.points[i];
          if (Math.hypot(p.x - world.x, p.y - world.y) < hitRadiusWorld) {
            foundHitId = `${line.id}-${i}`;
            foundHitType = 'node';
            setActiveGrip({ lineId: line.id, pointIndex: i });
            break;
          }
        }
        if (foundHitId) break;
      }
    }
    gestureRef.current = { isActive: true, pointerId: e.pointerId, startScreenPos: { x: e.clientX, y: e.clientY }, hitTargetId: foundHitId, hitTargetType: foundHitType, wasMoved: false };
    if (!foundHitType) {
      setIsPanning(true);
      setLastMousePos({ x: e.clientX, y: e.clientY });
      if (activeTool === ToolType.PAN) gestureRef.current.wasMoved = true;
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const coords = getCoords(e.clientX, e.clientY);
    const snapped = snapPoint(coords.world, { excludeId: draggingSymbolId || undefined });
    setCursorPos(snapped);
    setIsSnappedToSymbol(snapped.attachedSymbolId || null);
    if (!activePointers.current.has(e.pointerId)) return;
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.current.size >= 2) {
      const points = Array.from(activePointers.current.values()) as Point[];
      const dist = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      const midpoint = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
      if (lastPinchDist.current !== null && lastPinchMidpoint.current !== null) {
        const deltaScale = dist / lastPinchDist.current;
        const focalLocal = getLocalCoords(midpoint.x, midpoint.y);
        const currentScale = viewportRef.current.scale;
        const currentOffset = viewportRef.current.offset;
        const worldFocal = { x: (focalLocal.x - currentOffset.x) / currentScale, y: (focalLocal.y - currentOffset.y) / currentScale };
        const nextScale = Math.min(Math.max(0.05, currentScale * deltaScale), 15);
        const dx = midpoint.x - lastPinchMidpoint.current.x;
        const dy = midpoint.y - lastPinchMidpoint.current.y;
        setScale(nextScale);
        setOffset({ x: focalLocal.x - worldFocal.x * nextScale + dx, y: focalLocal.y - worldFocal.y * nextScale + dy });
      }
      lastPinchDist.current = dist;
      lastPinchMidpoint.current = midpoint;
      return;
    }
    if (!gestureRef.current.isActive || gestureRef.current.pointerId !== e.pointerId) return;
    const moveDist = Math.hypot(e.clientX - gestureRef.current.startScreenPos.x, e.clientY - gestureRef.current.startScreenPos.y);
    if (!gestureRef.current.wasMoved && moveDist > DRAG_THRESHOLD) {
      gestureRef.current.wasMoved = true;
      if (selection && selection.id !== gestureRef.current.hitTargetId) updateSelection(null);
    }
    if (gestureRef.current.wasMoved) {
      if (isPanning) {
        setOffset((prev: Point) => ({ x: prev.x + (e.clientX - lastMousePos.x), y: prev.y + (e.clientY - lastMousePos.y) }));
        setLastMousePos({ x: e.clientX, y: e.clientY });
      } else if (draggingSymbolId) {
        setMarkup((prev: MarkupState) => ({
          ...prev,
          symbols: prev.symbols.map((s: TradeSymbol) => s.id === draggingSymbolId ? { ...s, position: snapped } : s),
          lines: prev.lines.map((line: Line) => ({
            ...line,
            points: line.points.map((p: Point) => p.attachedSymbolId === draggingSymbolId ? { ...snapped, attachedSymbolId: draggingSymbolId } : p)
          }))
        }), false);
      } else if (activeGrip) {
        setMarkup((prev: MarkupState) => ({
          ...prev,
          lines: prev.lines.map(l => l.id === activeGrip.lineId ? {
            ...l,
            points: l.points.map((p, idx) => idx === activeGrip.pointIndex ? snapped : p)
          } : l)
        }), false);
      }
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    activePointers.current.delete(e.pointerId);
    if (activePointers.current.size < 2) { lastPinchDist.current = null; lastPinchMidpoint.current = null; }
    const { isActive, wasMoved, pointerId, hitTargetId, hitTargetType } = gestureRef.current;
    const wasLocked = gestureLock.current;
    if (pointerId === e.pointerId || activePointers.current.size === 0) {
      setIsPanning(false); setDraggingSymbolId(null); setActiveGrip(null);
      gestureRef.current.isActive = false; setIsSnappedToSymbol(null);
      if (activePointers.current.size === 0) gestureLock.current = false;
    }
    if (activePointers.current.size === 0) {
      if (!isActive || pointerId !== e.pointerId) return;
      const coords = getCoords(e.clientX, e.clientY);
      const snapped = snapPoint(coords.world, { excludeId: draggingSymbolId || undefined });
      const isTap = !wasMoved && !wasLocked;
      if (isTap) {
        if (activeTool === ToolType.PIPES) {
          setDraftPoints((prev) => prev ? [...prev, snapped] : [snapped]);
        } else if (activeTool === ToolType.SYMBOL) {
          if (hitTargetType === 'symbol') {
            updateSelection({ type: 'symbol', id: hitTargetId! });
          } else if (!hitTargetId) {
            if (selection) updateSelection(null);
            else setMarkup((prev: MarkupState) => ({ ...prev, symbols: [...prev.symbols, { id: Date.now().toString(), type: activeSymbol, position: snapped, system: activeSystem }] }));
          }
        } else {
          updateSelection(null);
        }
      } else if (wasMoved && !wasLocked) {
        setMarkup((prev: MarkupState) => ({ ...prev }), true);
      }
    }
  };

  return {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    resetGestures,
    isPanning,
    cursorPos,
    setCursorPos,
    isSnappedToSymbol,
    setIsSnappedToSymbol,
    activeGrip,
    setActiveGrip,
    draggingSymbolId,
  };
}
